import React, { useState } from "react";
import {
  MessageCircle,
  Bell,
  Check,
  X,
  Trash2,
  Calendar as CalIcon,
  Clock,
  MapPin,
  CheckCircle2,
  GraduationCap,
  Tag,
  Eye,
  Pencil,
  StickyNote,
  Send,
  ShieldCheck,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { formatDateES, computeClassBreakdown, studentCalendarLabelWide, isExamAppointment, appointmentRouteLabel } from "@/lib/utils-app";
import { APPOINTMENT_TYPE_SHORT } from "@/lib/appointment-types";
import WhatsAppPreviewDialog from "@/components/autoescuela/WhatsAppPreviewDialog";
import BulkWhatsAppDialog from "@/components/autoescuela/BulkWhatsAppDialog";

const STATUS_LABEL = {
  pending: "Pendiente",
  confirmed: "Confirmada",
  cancelled: "Cancelada",
  change_requested: "Cambio solicitado",
};

const STATUS_CLASS = {
  pending: "badge-pending",
  confirmed: "badge-confirmed",
  cancelled: "badge-rejected",
  change_requested: "badge-change",
};

function StatusBadges({ appt }) {  const typeKey = appt.appointment_type || "clase_practica";
  const typeLabel = APPOINTMENT_TYPE_SHORT[typeKey] || typeKey;
  return (
    <div className="flex items-center gap-2 mb-2 flex-wrap">
      <span className={STATUS_CLASS[appt.status] || "badge-pending"}>
        {STATUS_LABEL[appt.status] || appt.status}
        {appt.status === "cancelled"
          && (appt.appointment_type || "clase_practica") === "clase_practica"
          && (appt.cancel_consumes ? " · sí resta" : " · no resta")}
      </span>
      <span
        data-testid={`appt-type-badge-${appt.id}`}
        className="inline-flex items-center gap-1 rounded-full bg-neutral-100 text-neutral-700 px-2.5 py-0.5 text-xs font-semibold"
      >
        <Tag className="h-3 w-3" />
        {typeLabel}
      </span>
      {appt.attended && (
        <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 text-blue-800 px-2.5 py-0.5 text-xs font-semibold">
          <CheckCircle2 className="h-3 w-3" /> Asistió
        </span>
      )}
      {appt.whatsapp_sent && (
        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-800 px-2.5 py-0.5 text-xs font-semibold">
          WhatsApp enviado
        </span>
      )}
      <span
        data-testid={`reminder-badge-${appt.id}`}
        className={
          "inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold " +
          (appt.reminder_sent
            ? "bg-indigo-100 text-indigo-800"
            : "bg-neutral-100 text-neutral-600")
        }
      >
        <Bell className="h-3 w-3" />
        {appt.reminder_sent ? "Recordatorio enviado" : "Recordatorio pendiente"}
      </span>
    </div>
  );
}

function AppointmentMeta({ appt }) {
  const { numClasses } = computeClassBreakdown(appt.time, appt.end_time);
  return (
    <div className="text-sm text-neutral-500 mt-1 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
      <div className="flex items-center gap-1.5">
        <CalIcon className="h-4 w-4" /> {formatDateES(appt.date)}
      </div>
      <div className="flex items-center gap-1.5">
        <Clock className="h-4 w-4" />
        <span className="font-mono">{appt.time}</span>
        {appt.end_time && (
          <>
            <span className="text-neutral-400">–</span>
            <span className="font-mono">{appt.end_time}</span>
          </>
        )}
      </div>
      <div className="flex items-center gap-1.5 sm:col-span-2">
        <MapPin className="h-4 w-4" />
        <span className="truncate" data-testid={`appt-route-${appt.id}`}>
          {appointmentRouteLabel(appt)}
        </span>
      </div>
      {numClasses > 0 && (
        <div
          className="flex items-center gap-1.5 text-orange-700 font-semibold"
          data-testid={`appt-classes-count-${appt.id}`}
        >
          <GraduationCap className="h-4 w-4" />
          {numClasses} {numClasses === 1 ? "clase" : "clases"} de 45 min
        </div>
      )}
    </div>
  );
}

function AppointmentActions({
  appt,
  onConfirm,
  onReject,
  onToggleAttended,
  onDelete,
  onModify,
  onSaveNotes,
  onSetCancelConsumes,
  onStudentClick,
  onReload,
}) {
  // V1.38.5 · Observaciones inline (edición al enfocar)
  const [notes, setNotes] = useState(appt.notes || "");
  const [editing, setEditing] = useState(false);
  const [savingNotes, setSavingNotes] = useState(false);
  // V1.83 · Al cancelar una clase práctica hay que elegir si resta o no.
  const [cancelOpen, setCancelOpen] = useState(false);
  const isPracticeClass = (appt.appointment_type || "clase_practica") === "clase_practica";
  React.useEffect(() => { setNotes(appt.notes || ""); }, [appt.notes, appt.id]);
  const commit = async () => {
    setEditing(false);
    if ((notes || "") === (appt.notes || "") || !onSaveNotes) return;
    setSavingNotes(true);
    try { await onSaveNotes(notes); } finally { setSavingNotes(false); }
  };
  return (
    <div className="flex flex-wrap items-center gap-2 mt-4 pt-4 border-t border-neutral-100">
      {appt.status !== "confirmed" && (
        <button
          data-testid={`confirm-btn-${appt.id}`}
          onClick={onConfirm}
          className="btn-ghost border border-neutral-200 text-green-700"
        >
          <Check className="h-4 w-4" /> Confirmar
        </button>
      )}
      {appt.status !== "cancelled" && (
        isPracticeClass ? (
          <div className="inline-flex items-center gap-1.5">
            <button
              data-testid={`reject-btn-${appt.id}`}
              onClick={() => setCancelOpen((v) => !v)}
              className="btn-ghost border border-neutral-200 text-red-700"
            >
              <X className="h-4 w-4" /> Cancelar
            </button>
            {cancelOpen && (
              <span className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 bg-red-50 px-2 py-1" data-testid={`cancel-choice-${appt.id}`}>
                <span className="text-[11px] font-semibold text-red-800">¿Resta clase?</span>
                <button
                  data-testid={`cancel-no-resta-${appt.id}`}
                  onClick={() => { setCancelOpen(false); onReject?.(false); }}
                  className="h-7 px-2 rounded-md text-[11px] font-bold bg-white border border-emerald-300 text-emerald-800 hover:bg-emerald-50"
                >
                  NO resta
                </button>
                <button
                  data-testid={`cancel-si-resta-${appt.id}`}
                  onClick={() => { setCancelOpen(false); onReject?.(true); }}
                  className="h-7 px-2 rounded-md text-[11px] font-bold bg-white border border-red-300 text-red-700 hover:bg-red-100"
                >
                  SÍ resta
                </button>
              </span>
            )}
          </div>
        ) : (
          <button
            data-testid={`reject-btn-${appt.id}`}
            onClick={() => onReject?.(false)}
            className="btn-ghost border border-neutral-200 text-red-700"
          >
            <X className="h-4 w-4" /> Cancelar
          </button>
        )
      )}
      {/* V1.83 · Cita ya cancelada: se puede cambiar si resta o no resta. */}
      {appt.status === "cancelled" && isPracticeClass && onSetCancelConsumes && (
        <button
          data-testid={`cancel-consumes-toggle-${appt.id}`}
          onClick={() => onSetCancelConsumes(!appt.cancel_consumes)}
          className={
            "btn-ghost border " +
            (appt.cancel_consumes
              ? "border-red-300 bg-red-50 text-red-700"
              : "border-emerald-300 bg-emerald-50 text-emerald-800")
          }
          title="Cambia si esta cancelación descuenta la clase del bono del alumno"
        >
          <ShieldCheck className="h-4 w-4" />
          {appt.cancel_consumes ? "Cancelada · SÍ resta" : "Cancelada · NO resta"}
        </button>
      )}
      <button
        data-testid={`attended-btn-${appt.id}`}
        onClick={onToggleAttended}
        className="btn-ghost border border-neutral-200"
      >
        <CheckCircle2 className="h-4 w-4" />
        {appt.attended ? "Marcar no asistida" : "Marcar asistida"}
      </button>
      {/* V1.79 · Resultado del examen desde la agenda del profesor. */}
      {String(appt.appointment_type || "").startsWith("examen")
        && appt.date <= new Date().toISOString().slice(0, 10) && (
        <div className="inline-flex items-center gap-1.5">
          {appt.exam_result && (
            <span
              data-testid={`exam-result-badge-${appt.id}`}
              className={
                "px-1.5 py-0.5 rounded border text-[10px] font-bold " +
                (appt.exam_result === "apto"
                  ? "bg-emerald-100 text-emerald-800 border-emerald-300"
                  : appt.exam_result === "no_apto"
                  ? "bg-red-100 text-red-800 border-red-300"
                  : "bg-amber-100 text-amber-800 border-amber-300")
              }
            >
              {appt.exam_result === "apto" ? "APTO"
                : appt.exam_result === "no_apto" ? "NO APTO"
                : appt.exam_result === "no_presentado" ? (appt.exam_justified ? "No presentado (justif.)" : "No presentado")
                : "Anulado DGT"}
            </span>
          )}
          <select
            value={appt.exam_result || ""}
            data-testid={`exam-result-select-${appt.id}`}
            onChange={async (e) => {
              const v = e.target.value;
              if (!v) return;
              const [result, justified] = v === "no_presentado_justificado"
                ? ["no_presentado", true]
                : [v, false];
              const labels = {
                apto: "APTO", no_apto: "NO APTO",
                no_presentado: justified ? "No presentado (justificado)" : "No presentado",
                anulado_dgt: "Anulado por la DGT",
              };
              const extra = appt.exam_result
                ? "\n\nYa hay un resultado registrado: se pedirá el PIN maestro."
                : "";
              if (!window.confirm(`¿Marcar este examen como ${labels[result]}?${extra}`)) {
                e.target.value = appt.exam_result || "";
                return;
              }
              try {
                const { data } = await api.post(`/appointments/${appt.id}/exam-result`, { result, justified });
                toast.success(
                  data?.tasa_state?.label
                    ? `Resultado guardado · tasa: ${data.tasa_state.label}`
                    : "Resultado guardado",
                );
                if (onReload) onReload();
              } catch (err) {
                toast.error(formatApiError(err));
              }
            }}
            className="h-8 rounded-lg border border-neutral-300 bg-white px-2 text-[11px] font-semibold"
            title="Registrar el resultado del examen (afecta a la tasa y al expediente)"
          >
            <option value="">Resultado…</option>
            <option value="apto">APTO</option>
            <option value="no_apto">NO APTO</option>
            <option value="no_presentado">No presentado</option>
            <option value="no_presentado_justificado">No presentado (justificado)</option>
            <option value="anulado_dgt">Anulado por la DGT</option>
          </select>
        </div>
      )}
      {/* V1.76 · Falta justificada: la clase no resta del bono (pide PIN). */}
      {appt.attended === false && appt.date < new Date().toISOString().slice(0, 10)
        && (appt.appointment_type || "clase_practica") === "clase_practica" && (
        <button
          data-testid={`missed-exempt-btn-${appt.id}`}
          onClick={async () => {
            try {
              await api.patch(`/appointments/${appt.id}/missed-exempt`, { exempt: !appt.missed_exempt });
              toast.success(appt.missed_exempt ? "La falta vuelve a restar clase" : "Falta justificada: no resta clase");
              if (onReload) onReload();
            } catch (err) {
              toast.error(formatApiError(err));
            }
          }}
          className={
            "btn-ghost border " +
            (appt.missed_exempt
              ? "border-emerald-300 bg-emerald-50 text-emerald-800"
              : "border-neutral-200 text-neutral-700")
          }
          title="Requiere PIN maestro — la clase no se descuenta del bono del alumno"
        >
          <ShieldCheck className="h-4 w-4" />
          {appt.missed_exempt ? "No resta clase ✓" : "No restar clase"}
        </button>
      )}
      {/* V1.38.4 · "Modificar" para citas no canceladas. */}
      {appt.status !== "cancelled" && onModify && (
        <button
          data-testid={`modify-btn-${appt.id}`}
          onClick={onModify}
          className="btn-ghost border border-neutral-200 text-blue-700"
        >
          <Pencil className="h-4 w-4" /> Modificar
        </button>
      )}
      {/* V1.38.5 · Observaciones inline entre "Modificar" y el bote. */}
      {onSaveNotes && (
        <div
          data-testid={`notes-box-${appt.id}`}
          className={
            "flex-1 min-w-[220px] max-w-xl rounded-lg border px-2.5 py-1.5 " +
            (editing
              ? "border-orange-300 bg-orange-50/60"
              : notes
                ? "border-neutral-200 bg-neutral-50"
                : "border-dashed border-neutral-300 bg-white")
          }
        >
          <div className="flex items-center gap-1 text-[11px] text-neutral-500">
            <StickyNote className="h-3 w-3" />
            <span className="uppercase tracking-wider font-semibold">Observaciones:</span>
            {savingNotes && <span className="italic text-orange-600 ml-1">guardando…</span>}
          </div>
          <textarea
            data-testid={`notes-input-${appt.id}`}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            onFocus={() => setEditing(true)}
            onBlur={commit}
            placeholder="Añade una observación (Ctrl/Cmd+Enter para guardar)…"
            onKeyDown={(e) => {
              if ((e.ctrlKey || e.metaKey) && e.key === "Enter") { e.preventDefault(); e.target.blur(); }
            }}
            rows={editing || notes ? 2 : 1}
            className="w-full bg-transparent resize-none font-mono tabular-nums text-[10px] text-neutral-800 placeholder-neutral-400 focus:outline-none"
          />
        </div>
      )}
      <button
        data-testid={`delete-btn-${appt.id}`}
        onClick={onDelete}
        className="btn-ghost border border-neutral-200 text-red-700 ml-auto"
      >
        <Trash2 className="h-4 w-4" />
      </button>
    </div>
  );
}

export default function AppointmentCard({
  appt,
  onWhatsapp,
  onReminder,
  onConfirm,
  onReject,
  onToggleAttended,
  onDelete,
  onModify,
  onSaveNotes,
  onSetCancelConsumes,
  onStudentClick,
  onReload,
}) {
  const [preview, setPreview] = useState(false);
  const [showBulk, setShowBulk] = useState(false);
  const isShared = (appt.shared_students || []).length > 0;
  return (
    <div className="card-surface p-4 sm:p-5" data-testid={`appointment-card-${appt.id}`}>
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div className="min-w-0 flex-1">
          <StatusBadges appt={appt} />
          <div className="font-bold text-lg leading-tight">
            {onStudentClick ? (
              <button
                type="button"
                data-testid={`appt-student-link-${appt.id}`}
                onClick={() => onStudentClick(appt.student)}
                title="Ver sus horarios"
                className="text-left hover:text-orange-700 hover:underline decoration-dotted underline-offset-2"
              >
                {studentCalendarLabelWide(appt.student) || appt.student?.name || "—"}
              </button>
            ) : (studentCalendarLabelWide(appt.student) || appt.student?.name || "—")}
          </div>
          {isShared && (
            <div className="mt-2 space-y-2" data-testid={`shared-students-${appt.id}`}>
              <div className="inline-flex items-center gap-1.5 rounded-lg bg-blue-50 border border-blue-200 px-2 py-1 text-[11px] text-blue-900">
                <span className="font-semibold uppercase tracking-wide text-[9px] text-blue-700">
                  Sesión compartida
                </span>
                <span className="font-medium">
                  +{appt.shared_students.map((s) => s.name).join(", ")}
                </span>
              </div>
              <div className="flex flex-wrap gap-1.5" data-testid={`wa-preview-buttons-${appt.id}`}>
                <span className="text-[10px] uppercase tracking-wider font-semibold text-neutral-500 mt-1.5 mr-1">
                  Vista previa WhatsApp:
                </span>
                {(() => {
                  // Sort main + shared by pickup_time so button indexes match the ordered message list.
                  const list = [
                    { ...(appt.student || {}), pickup_time: appt.time, pickup_location: appt.pickup_location, is_main: true },
                    ...(appt.shared_students || []),
                  ].sort((a, b) => (a.pickup_time || "").localeCompare(b.pickup_time || ""));
                  return list.map((p, idx) => (
                    <button
                      key={p.id || idx}
                      type="button"
                      onClick={() => setPreview(true)}
                      data-testid={`wa-preview-btn-${appt.id}-${idx}`}
                      className="inline-flex items-center gap-1 rounded-full bg-white border border-emerald-300 text-emerald-800 hover:bg-emerald-50 text-[11px] font-semibold px-2.5 py-1 transition-colors"
                    >
                      <Eye className="h-3 w-3" /> Ver mensaje Alumno {idx + 1}
                    </button>
                  ));
                })()}
              </div>
            </div>
          )}
          <AppointmentMeta appt={appt} />
          {appt.status === "change_requested" && (appt.requested_date || appt.requested_time) && (
            <div
              data-testid={`change-request-notice-${appt.id}`}
              className="mt-3 rounded-xl bg-blue-50 border border-blue-200 px-3 py-2 text-xs text-blue-900"
            >
              <span className="font-semibold">El alumno propone:</span>{" "}
              <span className="font-mono">
                {appt.requested_date || "?"} · {appt.requested_time || "?"}
              </span>
            </div>
          )}
        </div>

        <div className="flex flex-wrap gap-2 sm:flex-col sm:items-end">
          <button
            data-testid={`whatsapp-btn-${appt.id}`}
            onClick={onWhatsapp}
            className="btn-whatsapp !h-10 !px-3"
          >
            <MessageCircle className="h-4 w-4" />
            WhatsApp
          </button>
          <button
            data-testid={`reminder-btn-${appt.id}`}
            onClick={onReminder}
            className="btn-secondary !h-10 !px-3"
          >
            <Bell className="h-4 w-4" />
            Recordar
          </button>
          {/* V1.35 · Envía en un único link todas las próximas citas del
              alumno (mismo diseño de confirmación individual, N tarjetas). */}
          <button
            data-testid={`bulk-whatsapp-btn-${appt.id}`}
            onClick={() => setShowBulk(true)}
            className="btn-secondary !h-10 !px-3"
            title="Enviar próximas sesiones del alumno en un solo enlace"
          >
            <Send className="h-4 w-4" />
            Próximas
          </button>
        </div>
      </div>

      <AppointmentActions
        appt={appt}
        onConfirm={onConfirm}
        onReject={onReject}
        onToggleAttended={onToggleAttended}
        onDelete={onDelete}
        onModify={onModify}
        onSaveNotes={onSaveNotes}
        onSetCancelConsumes={onSetCancelConsumes}
        onReload={onReload}
      />

      <WhatsAppPreviewDialog
        open={preview}
        appointment={appt}
        onClose={() => setPreview(false)}
        onSent={onReload}
      />

      <BulkWhatsAppDialog
        open={showBulk}
        onClose={() => setShowBulk(false)}
        studentId={appt.student?.id || appt.student_id}
        studentName={appt.student?.name || ""}
        phone={appt.student?.phone || ""}
      />
    </div>
  );
}

// V1.38.2 · Exports nombrados para reutilizar los badges/acciones
// en otros componentes (p. ej. panel del día en CalendarTab).
export { StatusBadges, AppointmentActions };
